import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import ScienceIcon from "@mui/icons-material/Science";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import NatureIcon from "@mui/icons-material/Nature";
import BadgeIcon from "@mui/icons-material/Badge";

const iconMap = {
  scientific_name: <ScienceIcon sx={{ color: "#2E7D32" }} />,
  common_name: <NatureIcon sx={{ color: "#2E7D32" }} />,
  local_name: <BadgeIcon sx={{ color: "#2E7D32" }} />,
  family: <LocalFloristIcon sx={{ color: "#2E7D32" }} />,
};

function BotanicalProfile({ plant }) {
  if (!plant?.botanical_profile) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 5,
        p: {
          xs: 3,
          md: 4,
        },
        borderRadius: 5,
        background:
          "linear-gradient(135deg,#FFFFFF,#F8FBFC)",
        border: "1px solid #E4EDF2",
        boxShadow: "0 10px 30px rgba(11,60,93,.08)",
      }}
    >
      {/* Header */}

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        mb={3}
      >
        <Box
          sx={{
            width: 55,
            height: 55,
            borderRadius: "50%",
            bgcolor: "#EEF5FA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <NatureIcon
            sx={{
              color: "#0B3C5D",
            }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: {
                xs: "1.5rem",
                md: "2rem",
              },
              fontWeight: 700,
              color: "#0B3C5D",
            }}
          >
            Botanical Profile
          </Typography>

          <Typography
            color="text.secondary"
            fontSize={15}
          >
            Basic botanical information of the documented species
          </Typography>
        </Box>
      </Stack>

      {/* Accent */}

      <Box
        sx={{
          width: 90,
          height: 4,
          bgcolor: "#43A047",
          borderRadius: 10,
          mb: 4,
        }}
      />

      {/* Information Cards */}

      <Grid container spacing={3}>
        {Object.entries(plant.botanical_profile).map(
          ([key, value]) => (
            <Grid
              item
              xs={12}
              sm={6}
              key={key}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  height: "100%",
                  background:
                    "linear-gradient(135deg,#FFFFFF,#F9FCFD)",
                  border: "1px solid #E5EDF3",
                  boxShadow:
                    "0 6px 18px rgba(11,60,93,.05)",
                  transition: ".35s",

                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow:
                      "0 15px 35px rgba(11,60,93,.12)",
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  mb={2}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      bgcolor: "#EEF8F2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {iconMap[key]}
                  </Box>

                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#0B3C5D",
                    }}
                  >
                    {key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) =>
                        c.toUpperCase()
                      )}
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    color: "#555",
                    lineHeight: 1.9,
                    wordBreak: "break-word",
                    fontSize: "1rem",

                    fontStyle:
                      key === "scientific_name"
                        ? "italic"
                        : "normal",
                  }}
                >
                  {value}
                </Typography>
              </Paper>
            </Grid>
          )
        )}
      </Grid>

      {/* Footer */}

      <Box
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 4,
          bgcolor: "#F5FAF7",
          borderLeft: "5px solid #43A047",
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        <NatureIcon
          sx={{
            color: "#2E7D32",
            mt: 0.3,
          }}
        />

        <Typography
          sx={{
            color: "#555",
            lineHeight: 1.9,
          }}
        >
          <strong style={{ color: "#0B3C5D" }}>
            Botanical Note:
          </strong>{" "}
          The botanical profile provides the accepted scientific
          name, common name, local name, and family classification
          of the species. This information follows standard
          botanical nomenclature and serves as the foundation for
          taxonomic identification, biodiversity documentation,
          and academic research.
        </Typography>
      </Box>
    </Paper>
  );
}

export default BotanicalProfile;